/*
 * ADDRESS FIELDS — the one address form of the storefront.
 *
 * The fields, their labels, the lookups and the validation depend on the
 * delivery country (address_style: 1 standard, 2 "Romanian" county/city
 * selects, 3 one autocomplete line; state_type, need_state / need_area /
 * need_post_code, post_code_regex, house_regex, post_code_local_name), so it
 * is the engine's, never a theme's: checkout, the saved addresses, the order
 * dialog and the invoice data all use this component.
 *
 *   <address-fields prefix="ship" :country="countryRow" :address="addr" :errors="errs"></address-fields>
 *
 *   country  {code, addressStyle, needPost, needState, needArea, stateType, postRegex, houseRegex, postLocalName}
 *   address  {postcode, state, area, city, street, house, address, houseFromAddress} - edited in place
 *   errors   {post, city, state, area, street, house} booleans, the form's own validation shows them
 *   prefix   ids: <prefix>-city ... (unique per page, the labels point at them)
 *
 * The theme only styles it (.form-group, .field-error, .pre-text, .autocomplete-*).
 */
import TomSelect_change_listener from "tom-select/dist/js/plugins/change_listener";
import TomSelect from "tom-select/dist/js/tom-select.base";

TomSelect.define('change_listener', TomSelect_change_listener);

const headers = () => (window.http && typeof http.headers === 'function') ? http.headers() : {'Content-Type': 'application/json', Accept: 'application/json'};
const post = (url, body) => fetch(url, {method: 'POST', headers: headers(), body: JSON.stringify(body)}).then((r) => r.json());

// states per country are loaded once (in-flight requests are shared)
const statesRequests = {};

// a missing translation group comes back as a plain string: no texts, not String.prototype methods
const TEXTS = () => (typeof window.ADDRESS_TEXTS === 'object' && window.ADDRESS_TEXTS) || {};

/*
 * A post code is typed in many ways - with or without a space, with a hyphen, in lower case, with the country
 * prefix ("LT-01234", "00-950", "sw1a 1aa") - while the country's regex accepts one shape (mostly the compact
 * one). Gives the form the regex accepts, or the typed text (upper case, trimmed) when no variant fits, so the
 * normal validation message shows. The typed form is kept when it already fits.
 */
export const normalizePostCode = (value, regex, countryCode = '') => {
    const typed = String(value ?? '').trim().toUpperCase().replace(/\s+/g, ' ');
    if (typed === '' || regex === null || regex === undefined || String(regex).length === 0) return typed;
    let re;
    try { re = new RegExp(regex); } catch (e) { return typed; }
    if (re.test(typed)) return typed;
    const compact = typed.replace(/[\s\-._]/g, '');
    const candidates = [];
    const prefix = String(countryCode ?? '').toUpperCase();
    if (prefix.length === 2 && compact.startsWith(prefix) && compact.length > 2) candidates.push(compact.slice(2));
    candidates.push(compact);
    // countries whose regex wants a separator: put a space, then a hyphen, at every position
    for (const separator of [' ', '-']) {
        for (let at = 1; at < compact.length; at++) candidates.push(compact.slice(0, at) + separator + compact.slice(at));
    }
    return candidates.find((candidate) => re.test(candidate)) ?? typed;
};

export const AddressFields = {
    name: 'AddressFields',
    delimiters: ['{{', '}}'],
    props: {
        prefix: {type: String, default: 'addr'},
        country: {type: Object, required: true},
        address: {type: Object, required: true},
        errors: {type: Object, default: () => ({})},
    },
    emits: ['changed'],
    data() {
        return {states: [], cities: [], postCodes: [], showPostCode: false, suggestions: [], showSuggestions: false, autoFilledCity: null};
    },
    computed: {
        t() { return TEXTS(); },
        style() { return Number(this.country.addressStyle) || 1; },
        needPost() { return this.country.needPost == 1; },
        needState() { return this.country.needState == 1; },
        needArea() { return this.country.needArea == 1; },
        stateLabel() {
            const t = this.t;
            return ({1: t.county, 2: t.region, 3: t.province})[this.country.stateType] ?? t.state;
        },
        usError() { return !!(this.errors.post || this.errors.city || this.errors.street); },
        showHouse() { return this.style !== 3 || ((this.address.street ?? '').length > 0 && !this.address.houseFromAddress); },
    },
    watch: {
        // a new country: new lists, new fields
        'country.code': {immediate: true, handler() { this.reset(); }},
        'country.addressStyle'() { this.$nextTick(() => this.mountSelects()); },
    },
    mounted() { this.mountSelects(); },
    beforeUnmount() { this.destroySelects(); },
    methods: {
        id(name) { return this.prefix + '-' + name; },
        // themes float the label of a filled field (.filled); the values arrive from lookups too, not only typing
        filled(key) {
            const field = {post: 'postcode'}[key] ?? key;
            return String(this.address[field] ?? '').length > 0;
        },
        aria(key) { return {'aria-invalid': this.errors[key] ? 'true' : 'false', 'aria-describedby': this.errors[key] ? this.id(key) + '-error' : null}; },
        reset() {
            this.states = []; this.cities = []; this.postCodes = []; this.showPostCode = false; this.suggestions = []; this.autoFilledCity = null;
            if (this.needState && this.country.code) this.loadStates();
            this.$nextTick(() => this.mountSelects());
        },
        loadStates() {
            const code = this.country.code;
            statesRequests[code] = statesRequests[code] ?? post('/api/countries/search-states', {country: code}).catch(() => { delete statesRequests[code]; return []; });
            statesRequests[code].then((data) => {
                if (code !== this.country.code) return;
                this.states = data;
                this.syncSelect('state');
                // a saved county: its cities
                if (this.style === 2 && this.address.state) this.loadCities(true);
            });
        },
        loadCities(keepCity) {
            if (!(this.address.state?.length > 0)) return;
            const code = this.country.code;
            const state = this.address.state;
            this.cities = [];
            this.clearSelect('city');
            post('/api/countries/search-cities', {country: code, state}).then((data) => {
                if (code !== this.country.code || state !== this.address.state) return;
                this.cities = data;
                this.syncSelect('city');
            }).catch(() => {});
        },
        onState() { this.loadCities(); this.$emit('changed', 'state'); },
        onCity() {
            // a city of the list knows its post code; otherwise the post code list is asked
            const postCode = this.cities.find((e) => e.name === this.address.city)?.post_code;
            if (postCode !== null && postCode !== undefined) {
                this.address.postcode = postCode;
            } else {
                this.postCodes = [];
                post('/api/countries/search-post-codes', {country: this.country.code, name: this.address.city}).then((d) => { this.postCodes = d; }).catch(() => {});
            }
            this.$emit('changed', 'city');
        },
        onHouse() {
            this.$emit('changed', 'house');
            if (!(this.needPost && this.style === 2)) return;
            const a = this.address;
            if ((a.house ?? '').length < 1 || (a.postcode ?? '').length > 0) {
                if ((a.postcode ?? '').length > 0) this.showPostCode = true;
                return;
            }
            post('/api/countries/post-code', {q: a.street + ' ' + a.house + ', ' + a.city + ', ' + a.state, country: this.country.code}).then((data) => {
                this.showPostCode = true;
                if (data.postalCode?.length > 0) a.postcode = data.postalCode;
            }).catch(() => { this.showPostCode = true; });
        },
        onPostCode() {
            this.address.postcode = normalizePostCode(this.address.postcode, this.country.postRegex, this.country.code);
            this.$emit('changed', 'post');
            setTimeout(() => {
                const regex = this.country.postRegex;
                if (regex !== null && regex !== undefined && String(regex).length > 0 && !new RegExp(regex).test(this.address.postcode ?? '')) return;
                const code = this.country.code;
                post('/api/countries/search-post-code', {country: code, post_code: this.address.postcode}).then((data) => {
                    if (code !== this.country.code) return;
                    const a = this.address;
                    if (this.needState && data?.state_name?.length > 0) a.state = data.state_name;
                    if (this.needArea && data?.county_name?.length > 0) a.area = data.county_name;
                    else if (this.needArea && data?.community_name?.length > 0) a.area = data.community_name;
                    // the city is filled only if the customer did not type one (or it came from a previous post code)
                    const typed = a.city ?? '';
                    if (data?.name?.length > 0 && (typed === '' || typed === this.autoFilledCity)) {
                        a.city = data.name;
                        this.autoFilledCity = data.name;
                    }
                }).catch(() => {});
            }, 80);
        },
        /* style 3: one line with suggestions */
        onAddressInput() {
            this.showSuggestions = true;
            clearTimeout(this._timer);
            if ((this.address.address ?? '').length < 4) { this.suggestions = []; return; }
            this._timer = setTimeout(() => {
                const seq = this._seq = (this._seq || 0) + 1;
                post('/api/countries/autocomplete', {q: this.address.address, country: this.country.code}).then((data) => {
                    if (seq === this._seq) this.suggestions = data.addresses ?? [];
                }).catch(() => {});
            }, 300);
        },
        pick(item) {
            const a = this.address;
            a.postcode = item.postalCode;
            a.city = item.city;
            a.street = item.street;
            a.state = item.state;
            a.area = item.borough;
            a.house = item.number ?? '';
            // without a house number in the suggestion the form asks for it
            a.houseFromAddress = (item.number ?? '').length > 0;
            a.address = item.formattedAddress;
            this.showSuggestions = false;
            this.$emit('changed', 'address');
        },

        /* style 2: searchable selects */
        select(name) { return this['_ts_' + name]; },
        // Tom Select owns the options of these selects (it rewrites the <select>'s children, so Vue does not render them)
        syncSelect(name) {
            const ts = this.select(name);
            if (!ts) return;
            const items = name === 'state'
                ? this.states.map((x) => ({value: x?.state_name, text: x?.state_name}))
                : this.cities.map((c) => ({value: c.name, text: c.community_name ? c.community_name + ' - ' + c.name : c.name}));
            try {
                ts.clearOptions();
                ts.addOptions(items.filter((i) => i.value));
                ts.refreshOptions(false);
                const current = this.address[name];
                if (current && items.some((i) => i.value === current)) ts.setValue(current, true);
            } catch (e) { /* the plain select stays */ }
        },
        clearSelect(name) {
            const ts = this.select(name);
            if (!ts) return;
            try { ts.clear(); ts.clearOptions(); ts.clearCache(); } catch (e) { /* not built yet */ }
        },
        mountSelects() {
            if (this.style !== 2) { this.destroySelects(); return; }
            for (const name of ['state', 'city']) {
                const el = document.getElementById(this.id(name));
                if (!el) continue;
                const wrapped = el.parentElement && el.parentElement.classList.contains('ts-wrapper');
                if (this.select(name) && wrapped) continue;
                try { this.select(name)?.destroy(); } catch (e) { /* gone */ }
                try {
                    const ts = new TomSelect(el, {plugins: ['change_listener']});
                    // the search field says it can be typed in (phones open it as a list)
                    if (ts.control_input && this.t.search) ts.control_input.setAttribute('placeholder', this.t.search);
                    // on a phone the list is a sheet over the page: the page behind is locked (co-layer-open)
                    ts.on('dropdown_open', () => { if (document.body.offsetWidth < 492) { document.body.classList.add('no-scroll'); document.documentElement.classList.add('co-layer-open'); } });
                    ts.on('dropdown_close', () => { document.body.classList.remove('no-scroll'); document.documentElement.classList.remove('co-layer-open'); });
                    this['_ts_' + name] = ts;
                    this.syncSelect(name);
                } catch (e) { /* the plain select stays */ }
            }
        },
        destroySelects() {
            for (const name of ['state', 'city']) {
                try { this['_ts_' + name]?.destroy(); } catch (e) { /* gone */ }
                this['_ts_' + name] = null;
            }
        },
    },
    template: `
<div class="address-fields">
    <div class="pure-g no-container d-flex" v-if="style == 2">
        <div v-show="needState" class="flex-10">
            <div class="form-group" :class="{error: errors.state, filled: filled('state')}">
                <label :for="id('state')" v-text="stateLabel"></label>
                <select :id="id('state')" v-model="address.state" @change="onState" autocomplete="address-level1" :aria-required="needState ? 'true' : 'false'" v-bind="aria('state')">
                </select>
            </div>
            <p :id="id('state-error')" class="field-error" v-if="errors.state">{{ t.missing_state }}</p>
        </div>
        <div class="flex-10">
            <div class="form-group" :class="{error: errors.city, filled: filled('city')}">
                <label :for="id('city')">{{ t.city }}</label>
                <select :id="id('city')" v-model="address.city" @change="onCity" autocomplete="address-level2" aria-required="true" v-bind="aria('city')">
                </select>
            </div>
            <p :id="id('city-error')" class="field-error" v-if="errors.city">{{ t.missing_city }}</p>
        </div>
        <div v-show="needArea" class="flex-10">
            <div class="form-group" :class="{error: errors.area, filled: filled('area')}">
                <label :for="id('area')">{{ t.area }}</label>
                <input :id="id('area')" type="text" v-model="address.area" v-bind="aria('area')">
            </div>
            <p :id="id('area-error')" class="field-error" v-if="errors.area">{{ t.missing_area }}</p>
        </div>
    </div>

    <div class="no-container d-flex flex-wrap" v-else-if="style == 3">
        <div class="form-group autocomplete-container" :class="{error: usError, filled: filled('address')}">
            <label :for="id('address')">{{ t.address }}</label>
            <input :id="id('address')" type="text" v-model="address.address" @input="onAddressInput" autocomplete="off"
                   role="combobox" aria-autocomplete="list" aria-required="true" :aria-expanded="showSuggestions && suggestions.length > 0 ? 'true' : 'false'" :aria-controls="id('address-list')"
                   :aria-invalid="usError ? 'true' : 'false'" :aria-describedby="usError ? id('address-error') : null">
            <ul :id="id('address-list')" class="autocomplete-list" role="listbox" v-if="showSuggestions && suggestions.length > 0">
                <li v-for="item in suggestions" :key="item.id" role="option">
                    <button type="button" class="btn btn-transparent" @click="pick(item)">{{ item.formattedAddress }}</button>
                </li>
            </ul>
        </div>
        <p :id="id('address-error')" class="field-error" v-if="usError">{{ t.missing_address }}</p>
    </div>

    <div class="pure-g no-container d-flex" v-else>
        <div v-show="needPost" class="flex-10">
            <div class="form-group" :class="{error: errors.post, filled: filled('post')}">
                <label v-if="country.postLocalName" :for="id('postal-code')" v-html="country.postLocalName"></label>
                <label v-else :for="id('postal-code')">{{ t.post_code }}</label>
                <input :id="id('postal-code')" type="text" v-model="address.postcode" @change="onPostCode" :pattern="country.postRegex" autocomplete="postal-code" :aria-required="needPost ? 'true' : 'false'" v-bind="aria('post')">
            </div>
            <p :id="id('post-error')" class="field-error" v-if="errors.post">{{ t.missing_post_code }}</p>
        </div>
        <div v-show="needState" class="flex-12">
            <div class="form-group" :class="{error: errors.state, filled: filled('state')}">
                <label :for="id('state')" v-text="stateLabel"></label>
                <input :id="id('state')" type="text" :list="id('state-list')" v-model="address.state" autocomplete="address-level1" :aria-required="needState ? 'true' : 'false'" v-bind="aria('state')">
            </div>
            <p :id="id('state-error')" class="field-error" v-if="errors.state">{{ t.missing_state }}</p>
        </div>
        <div v-show="needArea" class="flex-12">
            <div class="form-group" :class="{error: errors.area, filled: filled('area')}">
                <label :for="id('area')">{{ t.area }}</label>
                <input :id="id('area')" type="text" v-model="address.area" autocomplete="address-level2" v-bind="aria('area')">
            </div>
            <p :id="id('area-error')" class="field-error" v-if="errors.area">{{ t.missing_area }}</p>
        </div>
        <div class="flex-12">
            <div class="form-group" :class="{error: errors.city, filled: filled('city')}">
                <label :for="id('city')">{{ t.city }}</label>
                <input :id="id('city')" type="text" v-model="address.city" autocomplete="address-level2" aria-required="true" v-bind="aria('city')">
            </div>
            <p :id="id('city-error')" class="field-error" v-if="errors.city">{{ t.missing_city }}</p>
        </div>
    </div>

    <div class="pure-g no-container d-flex" v-if="style != 3">
        <div class="flex-16">
            <div class="form-group" :class="{error: errors.street, filled: filled('street')}">
                <label :for="id('street')">{{ t.street }}</label>
                <input :id="id('street')" type="text" v-model="address.street" autocomplete="address-line1" aria-required="true" v-bind="aria('street')">
            </div>
            <p :id="id('street-error')" class="field-error" v-if="errors.street">{{ t.missing_street }}</p>
        </div>
        <div class="flex-12">
            <div class="form-group" :class="{error: errors.house, filled: filled('house')}">
                <label :for="id('house')">{{ t.number }}</label>
                <span class="pre-text" v-if="style == 2" aria-hidden="true">nr</span>
                <input :id="id('house')" type="text" v-model="address.house" :pattern="country.houseRegex" @change="onHouse" autocomplete="address-line2" aria-required="true" v-bind="aria('house')">
            </div>
            <p :id="id('house-error')" class="field-error" v-if="errors.house">{{ t.missing_house }}</p>
        </div>
        <div v-if="needPost && style == 2 && showPostCode" class="flex-10">
            <div class="form-group" :class="{error: errors.post, filled: filled('post')}">
                <label v-if="country.postLocalName" :for="id('postal-code')" v-html="country.postLocalName"></label>
                <label v-else :for="id('postal-code')">{{ t.post_code }}</label>
                <input :id="id('postal-code')" :list="id('postcode-list')" type="text" v-model="address.postcode" @change="onPostCode" :pattern="country.postRegex" autocomplete="postal-code" aria-required="true" v-bind="aria('post')">
            </div>
            <p :id="id('post-error')" class="field-error" v-if="errors.post">{{ t.missing_post_code }}</p>
        </div>
    </div>

    <div class="no-container d-flex flex-wrap" v-if="style == 3 && showHouse">
        <div class="form-group" :class="{error: errors.house, filled: filled('house')}">
            <label :for="id('house')">{{ t.number }}</label>
            <input :id="id('house')" type="text" v-model="address.house" :pattern="country.houseRegex" autocomplete="address-line2" aria-required="true" v-bind="aria('house')">
        </div>
        <p :id="id('house-error')" class="field-error" v-if="errors.house">{{ t.missing_house }}</p>
    </div>

    <datalist :id="id('state-list')" v-if="states.length > 0"><option :value="s?.state_name" v-for="s in states" :key="s?.state_name"></option></datalist>
    <datalist :id="id('postcode-list')" v-if="postCodes.length > 0"><option :value="c?.post_code" v-for="c in postCodes" :key="c?.post_code"></option></datalist>
</div>`,
};

window.AddressFields = AddressFields;
export default AddressFields;

/*
 * Helpers for pages that have only a country id / code (not the checkout's
 * country rows): the row from /api/countries, and the completeness rule.
 */
let countryDocs = null;
AddressFields.row = (doc) => ({
    id: doc.id, code: doc.code, is_eu: doc.is_eu == 1,
    needPost: doc.need_post_code, needArea: doc.need_area, needState: doc.need_state, stateType: doc.state_type,
    addressStyle: doc.address_style, postRegex: doc.post_code_regex, houseRegex: doc.house_regex, postLocalName: doc.post_code_local_name,
});
AddressFields.country = async (idOrCode) => {
    if (!countryDocs) {
        countryDocs = fetch('/api/countries', {headers: headers()}).then((r) => r.json()).catch(() => { countryDocs = null; return []; });
    }
    const list = await countryDocs;
    const key = String(idOrCode ?? '').toLowerCase();
    const doc = (list || []).find((c) => String(c.id) === key || String(c.code ?? '').toLowerCase() === key);
    return doc ? AddressFields.row(doc) : null;
};
AddressFields.blank = (a = {}) => ({
    postcode: a.post_code ?? a.postcode ?? '', city: a.city ?? '', street: a.street ?? '', house: a.house ?? '',
    state: a.state ?? '', area: a.area ?? '', address: a.address ?? '', houseFromAddress: false,
});
// which fields are missing (the same keys as the component's errors)
AddressFields.missing = (country, a) => {
    const empty = (v) => !(String(v ?? '').trim().length > 0);
    const out = {post: false, city: false, state: false, area: false, street: false, house: false};
    if (!country) { out.post = empty(a.postcode); out.city = empty(a.city); out.street = empty(a.street); out.house = empty(a.house); return out; }
    const style = Number(country.addressStyle) || 1;
    out.city = empty(a.city);
    out.street = empty(a.street);
    out.house = empty(a.house);
    if (country.needPost == 1) out.post = empty(a.postcode) || (country.postRegex ? !new RegExp(country.postRegex).test(normalizePostCode(a.postcode, country.postRegex, country.code)) : false);
    if (country.needState == 1) out.state = empty(a.state);
    if (country.needArea == 1) out.area = empty(a.area);
    if (style === 3) out.street = out.street || out.city;
    return out;
};
AddressFields.isComplete = (country, a) => !Object.values(AddressFields.missing(country, a)).some(Boolean);
